import type { Channel, ChannelEvents, ChannelFactory, ChannelSpec } from './types';

/** ICE `disconnected` for this long counts as a failed session (spec 6). */
export const DISCONNECTED_MS = 5000;

const base = (url: string) => url.replace(/\/+$/, '');

export const openBrowserChannel: ChannelFactory = (spec, events) => (spec.transport === 'tcp' ? openWebSocket(spec, events) : openDataChannel(spec, events));

/**
 * The transport protocol of the selected ICE candidate pair. Chrome fills `transport.selectedCandidatePairId`;
 * Firefox may not, so a nominated succeeded pair is the fallback. Undefined when the stats say nothing usable.
 */
async function selectedProtocol(pc: RTCPeerConnection): Promise<string | undefined> {
    try {
        const stats = await pc.getStats();
        let pairId: string | undefined;
        stats.forEach((s) => {
            if (s.type === 'transport' && s.selectedCandidatePairId) pairId = s.selectedCandidatePairId;
        });
        if (!pairId)
            stats.forEach((s) => {
                if (s.type === 'candidate-pair' && s.nominated && s.state === 'succeeded') pairId = s.id;
            });
        const pair = pairId ? stats.get(pairId) : undefined;
        const local = pair ? stats.get(pair.localCandidateId) : undefined;
        return typeof local?.protocol === 'string' ? local.protocol : undefined;
    } catch {
        return undefined;
    }
}

/**
 * One RTCPeerConnection with one unordered, zero-retransmit data channel, signalled with a single
 * POST to the probe server (spec 5). The server is ICE-lite and answers the browser's checks, so the
 * offer is posted at once without waiting for candidate gathering.
 */
function openDataChannel(spec: ChannelSpec, events: ChannelEvents): Channel {
    const pc = new RTCPeerConnection();
    const dc = pc.createDataChannel(spec.kind, { ordered: false, maxRetransmits: 0 });
    dc.binaryType = 'arraybuffer';
    dc.bufferedAmountLowThreshold = 0;
    let closed = false;
    let disconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const finish = (reason: string, notify: boolean) => {
        if (closed) return;
        closed = true;
        clearTimeout(disconnectTimer);
        pc.close();
        if (notify) events.onClose(reason);
    };

    dc.onopen = () => {
        if (closed) return;
        void selectedProtocol(pc).then((protocol) => {
            if (closed) return;
            if (protocol !== undefined && protocol !== 'udp') finish('not_udp', true);
            else events.onOpen();
        });
    };
    dc.onmessage = (e: MessageEvent) => {
        if (!closed && e.data instanceof ArrayBuffer) events.onMessage(e.data);
    };
    dc.onbufferedamountlow = () => {
        if (!closed) events.onDrain?.();
    };
    dc.onclose = () => finish('closed', true);
    pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed') finish('failed', true);
        else if (pc.connectionState === 'disconnected') disconnectTimer ??= setTimeout(() => finish('disconnected', true), DISCONNECTED_MS);
        else if (pc.connectionState === 'connected') {
            clearTimeout(disconnectTimer);
            disconnectTimer = undefined;
        }
    };

    void (async () => {
        try {
            await pc.setLocalDescription(await pc.createOffer());
            const res = await fetch(`${base(spec.url)}/session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: spec.token, offer: pc.localDescription?.sdp, kind: spec.kind })
            });
            const body = (await res.json().catch(() => ({}))) as { answer?: string; error?: string };
            if (closed) return;
            if (!res.ok || !body.answer) return finish(body.error ?? `http_${res.status}`, true);
            await pc.setRemoteDescription({ type: 'answer', sdp: body.answer });
        } catch {
            finish('signaling', true);
        }
    })();

    return {
        transport: 'udp',
        send: (data) => {
            if (!closed && dc.readyState === 'open') dc.send(data);
        },
        bufferedAmount: () => dc.bufferedAmount,
        close: () => finish('client', false)
    };
}

/** The TCP fallback (spec 4.5): first message the token, then binary probes and echoes. */
function openWebSocket(spec: ChannelSpec, events: ChannelEvents): Channel {
    // The server's /ws only serves probes; a load session would replace the probe session.
    if (spec.kind === 'load') {
        let refused = true;
        queueMicrotask(() => {
            if (refused) events.onClose('kind');
        });
        return { transport: 'tcp', send: () => {}, bufferedAmount: () => 0, close: () => (refused = false) };
    }
    const ws = new WebSocket(`${base(spec.url).replace(/^http/, 'ws')}/ws`);
    ws.binaryType = 'arraybuffer';
    let closed = false;

    const finish = (reason: string, notify: boolean) => {
        if (closed) return;
        closed = true;
        try {
            ws.close();
        } catch {
            // already closing
        }
        if (notify) events.onClose(reason);
    };

    ws.onopen = () => {
        if (closed) return;
        ws.send(spec.token);
        events.onOpen();
    };
    ws.onmessage = (e: MessageEvent) => {
        if (!closed && e.data instanceof ArrayBuffer) events.onMessage(e.data);
    };
    // The close event carries the server's reason ("banned", "full", ...); an error is always followed by it.
    ws.onclose = (e: CloseEvent) => finish(e.reason || 'closed', true);

    return {
        transport: 'tcp',
        send: (data) => {
            if (!closed && ws.readyState === WebSocket.OPEN) ws.send(data);
        },
        bufferedAmount: () => ws.bufferedAmount,
        close: () => finish('client', false)
    };
}
